<script>
import Swal from "sweetalert2";
import { Modal } from "bootstrap";
import { useSharedStore } from "@/stores/shared-store";
import { useCourseFetchStore } from "@/stores/course/course-fetch-store";
import { useCourseDataStore } from "@/stores/course/course-data-store";
import { useAuthStore } from "@/stores/auth-store";
import { useEnrollmentStore } from "@/stores/access/enrollment-store.js";
import { useAccessClassRequestStore } from "@/stores/access/access-class-request-store";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import certificates from "@/components/certificates.vue";
import { useExamDataStore } from "@/stores/exam/exam-data-store";
import { useLearningPathStore } from "@/stores/learning-path-store";
import BaseLoading from "@/components/CustomComponent/shared/Loading.vue";

export default {
  components: {
    certificates,
    BaseLoading,
  },
  data() {
    return {
      courseId: this.$route.params.course_id,
      isApproving: false,
      isDownloadingCert: false,
    };
  },
  computed: {
    authStore() {
      return useAuthStore();
    },
    sharedStore() {
      return useSharedStore();
    },
    courseFetchStore() {
      return useCourseFetchStore();
    },
    CourseDataStore() {
      return useCourseDataStore();
    },
    enrollmentStore() {
      return useEnrollmentStore();
    },
    accessClassRequestStore() {
      return useAccessClassRequestStore();
    },
    examDataStore() {
      return useExamDataStore();
    },
    alreadyEnrolled() {
      return this.enrollmentStore.enrollmentStudent.some(
        (enrollment) => String(enrollment.courseID?.course_id) === this.courseId
      );
    },
    currentEnrollment() {
      return this.enrollmentStore.enrollmentStudent.find(
        (enrollment) => String(enrollment.courseID?.course_id) === this.courseId
      );
    },
    expiredDate() {
      if (!this.currentEnrollment || !this.currentEnrollment.expiredAt)
        return null;
      return new Date(this.currentEnrollment.expiredAt);
    },
    isExpired() {
      const now = new Date();
      if (!this.currentEnrollment) return false;
      if (!this.currentEnrollment.expiredAt) return true;
      const expiredAt = new Date(this.currentEnrollment.expiredAt);
      return expiredAt > now;
    },

    isCompleted() {
      return this.currentEnrollment?.status === "Completed";
    },

    canResumeCourse() {
      const role = this.authStore.role?.toLowerCase();
      const isPrincipal = role === "principal";
      const isInstructor = role === "teacher";
      const isStudent = role === "student";
      const enrolled = this.alreadyEnrolled;
      const isExpired = this.isExpired;
      const isCompleted = this.isCompleted;

      // Student: boleh resume/review jika enrolled dan (belum expired ATAU sudah Completed)
      if (isStudent) {
        return enrolled && (isExpired || isCompleted);
      }

      // Principal & Instructor/Teacher: selalu bisa review
      if (isPrincipal || isInstructor) {
        return true;
      }

      return false;
    },

    canApproveCourse() {
      const role = this.authStore.role?.toLowerCase();
      const status = (
        this.courseFetchStore.basicCourseDetail?.status || ""
      )
        .toLowerCase()
        .trim();
      return (
        role === "principal" &&
        (status === "new" ||
          status === "need approval edit" ||
          (status.includes("approval") && status.includes("edit")) ||
          status.includes("need edit"))
      );
    },
    isStudent() {
      return this.authStore.role?.toLowerCase() === "student";
    },
    learningPathStore() {
      return useLearningPathStore();
    },
    isCourseInAssignedLearningPath() {
      if (!this.isStudent) return true;
      const targetCourseId = Number(this.courseId);
      return (this.learningPathStore.studentPaths || []).some((path) =>
        (path.items || []).some(
          (item) => Number(item.course) === targetCourseId
        )
      );
    },
    canEnrollCourse() {
      return (
        !this.alreadyEnrolled &&
        this.isStudent &&
        this.courseFetchStore.basicCourseDetail?.status === "Active" &&
        this.isCourseInAssignedLearningPath
      );
    },
    canRequestAccess() {
      const isStudent = this.authStore.role?.toLowerCase() === "student";
      if (!isStudent) return false;

      // Hanya muncul kalau student sudah enroll course ini
      if (!this.currentEnrollment) return false;
      // Kalau status enrollment = "Expired", munculkan tombol
      return this.currentEnrollment.status === "Expired";
    },
    canRemedialExam() {
      // Hanya muncul kalau student sedang remedial
      const isStudent = this.authStore.role?.toLowerCase() === "student";
      if (!isStudent || !this.currentEnrollment) return false;

      return this.currentEnrollment.status === "Remedial";
    },
    canGenerateCertificate() {
      const isStudent = this.authStore.role?.toLowerCase() === "student";
      if (!isStudent || !this.currentEnrollment) return false;

      return this.currentEnrollment.status === "Completed";
    },
    hasPendingAccessRequest() {
      const studentId = this.authStore.currentUser?.id;
      const targetCourseId =
        this.courseId || this.courseFetchStore.basicCourseDetail?.course_id;
      if (!studentId || !targetCourseId) return false;

      return (this.accessClassRequestStore.classRequestList || []).some((req) => {
        const reqStudentId = req.studentID?.id ?? req.studentID;
        const reqCourseId =
          req.courseID?.course_id ?? req.course?.course_id ?? req.courseID;
        const status = (req.status || "").toLowerCase();
        return (
          String(reqStudentId) === String(studentId) &&
          String(reqCourseId) === String(targetCourseId) &&
          status === "pending"
        );
      });
    },
    resumeCourseLabel() {
      const role = this.authStore.role?.toLowerCase();
      if (["principal", "instructor", "teacher"].includes(role)) {
        return "Review Course";
      }
      if (this.currentEnrollment?.status === "Completed") {
        return "Review Course";
      }
      return "Resume Course";
    },
    /**
     * Single dominant CTA. Priority:
     * Approve > Start Exam > Resume/Review > Enroll > Request Access > Certificate
     */
    primaryAction() {
      if (this.canApproveCourse) {
        return {
          key: "approve",
          label: "Approve Course",
          btnClass: "btn-success",
          icon: "isax isax-tick-circle",
        };
      }
      if (this.canRemedialExam) {
        return {
          key: "exam",
          label: "Start Exam",
          btnClass: "btn-primary",
        };
      }
      if (this.canResumeCourse) {
        return {
          key: "resume",
          label: this.resumeCourseLabel,
          btnClass: "btn-success",
        };
      }
      if (this.canEnrollCourse) {
        return {
          key: "enroll",
          label: "Enroll Now",
          btnClass: "btn-primary",
        };
      }
      if (this.canRequestAccess && !this.hasPendingAccessRequest) {
        return {
          key: "request",
          label: "Request Access Course",
          btnClass: "btn-danger",
        };
      }
      if (this.canGenerateCertificate) {
        return {
          key: "certificate",
          label: "Certificate",
          btnClass: "btn-warning",
        };
      }
      return null;
    },
    /** Remaining actions shown as quieter outline controls under the primary CTA. */
    secondaryActions() {
      const primaryKey = this.primaryAction?.key;
      const actions = [];

      if (this.canResumeCourse && primaryKey !== "resume") {
        actions.push({
          key: "resume",
          label: this.resumeCourseLabel,
          btnClass: "btn-outline-success",
        });
      }
      if (this.canEnrollCourse && primaryKey !== "enroll") {
        actions.push({
          key: "enroll",
          label: "Enroll Now",
          btnClass: "btn-outline-primary",
        });
      }
      if (
        this.canRequestAccess &&
        !this.hasPendingAccessRequest &&
        primaryKey !== "request"
      ) {
        actions.push({
          key: "request",
          label: "Request Access Course",
          btnClass: "btn-outline-danger",
        });
      }
      if (this.canRemedialExam && primaryKey !== "exam") {
        actions.push({
          key: "exam",
          label: "Start Exam",
          btnClass: "btn-outline-primary",
        });
      }
      if (this.canGenerateCertificate && primaryKey !== "certificate") {
        actions.push({
          key: "certificate",
          label: "Certificate",
          btnClass: "btn-outline-warning",
        });
      }

      return actions;
    },
  },
  watch: {
    "authStore.currentUser": {
      immediate: true,
      handler(newVal) {
        if (newVal?.id && this.authStore.role?.toLowerCase() === "student") {
          this.enrollmentStore.fetchEnrollmentStudentList(newVal.id);
          this.accessClassRequestStore.fetchAccessRequests({
            studentID: newVal.id,
          });
          this.learningPathStore.fetchStudentPaths();
        }
      },
    },
    "enrollmentStore.enrollmentStudent": {
      handler() {
        if (!this.courseFetchStore.basicCourseDetail?.course_id) {
          this.seedCourseFromEnrollment();
        }
      },
    },
  },
  created() {
    this.courseFetchStore.fetchDifficultyList();
    this.authStore.authUser();
    this.hydrateCourseDetail();
    this.courseFetchStore.fetchCourseList();

    if (this.authStore.role?.toLowerCase() === "student") {
      const studentId = this.authStore.currentUser?.id;
      if (studentId) {
        this.enrollmentStore.fetchEnrollmentStudentList(studentId);
        this.accessClassRequestStore.fetchAccessRequests({
          studentID: studentId,
        });
        this.learningPathStore.fetchStudentPaths();
      }
    }
  },
  methods: {
    seedCourseFromEnrollment() {
      const enrolled = this.enrollmentStore.enrollmentStudent?.find(
        (enrollment) =>
          String(enrollment.courseID?.course_id) === String(this.courseId)
      );
      if (enrolled?.courseID && typeof enrolled.courseID === "object") {
        this.courseFetchStore.setBasicCourseDetail(enrolled.courseID);
      }
    },
    async hydrateCourseDetail() {
      this.seedCourseFromEnrollment();
      await this.courseFetchStore.fetchCourseDetailBasic(this.courseId);
      if (!this.courseFetchStore.basicCourseDetail?.course_id) {
        this.seedCourseFromEnrollment();
      }
    },
    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.toLocaleDateString("id-ID", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    async confirmApproveInDetail() {
      const course = this.courseFetchStore.basicCourseDetail;
      const result = await Swal.fire({
        title: "Konfirmasi Approval",
        text: `Apakah Anda yakin ingin menyetujui (approve) course "${course?.title || ''}"?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Ya, Approve!",
        cancelButtonText: "Batal",
        confirmButtonColor: "#28a745",
        cancelButtonColor: "#6c757d",
      });

      if (result.isConfirmed) {
        try {
          this.isApproving = true;
          await this.CourseDataStore.approveCourse(this.courseId);
          await this.courseFetchStore.fetchCourseDetailBasic(this.courseId);
          document.getElementById("closeApproveDetailModalBtn")?.click();
          Swal.fire({
            icon: "success",
            title: "Berhasil Disetujui!",
            text: `Course "${course?.title || ''}" berhasil di-approve.`,
            timer: 2000,
            showConfirmButton: false,
          });
        } catch (error) {
          console.error("Gagal approve course:", error);
          Swal.fire("Gagal!", "Terjadi kesalahan saat menyetujui course.", "error");
        } finally {
          this.isApproving = false;
        }
      }
    },
    handleApprove() {
      const modalEl = document.getElementById("approveCourseModalDetail");
      if (modalEl) {
        const modal = new Modal(modalEl);
        modal.show();
      }
    },
    handleResumeClick() {
      const role = this.authStore.role?.toLowerCase();
      const courseID =
        this.courseId || this.courseFetchStore.basicCourseDetail?.course_id;
      if (!courseID) {
        this.sharedStore.showError(
          "Course tidak ditemukan. Silakan refresh halaman."
        );
        return;
      }

      if (role === "principal" || role === "teacher") {
        this.handleResume(courseID, null);
        return;
      }

      const enrollmentID = this.currentEnrollment?.enrollmentID;
      if (!enrollmentID) {
        this.sharedStore.showError(
          "Data enrollment tidak ditemukan. Silakan refresh halaman."
        );
        return;
      }
      this.handleResume(courseID, enrollmentID);
    },
    handleCourseAction(key) {
      switch (key) {
        case "resume":
          this.handleResumeClick();
          break;
        case "enroll":
          this.handleEnroll(this.courseId);
          break;
        case "request":
          this.handleRequestAccessCourse(this.courseId);
          break;
        case "exam":
          this.goToExam();
          break;
        case "certificate":
          this.openCertificateModal();
          break;
        default:
          break;
      }
    },
    handleResume(courseID, enrollmentID) {
      let slug;
      const role = this.authStore.role?.toLowerCase();
      if (role === "principal" || role === "teacher") {
        // principal & instructor cukup pakai courseID saja
        slug = btoa(`${courseID}`);
      } else {
        // student tetap pakai courseID-enrollmentID
        slug = btoa(`${courseID}-${enrollmentID}`);
      }
      this.$router.push({
        name: "CourseWatch",
        params: {
          slug,
        },
      });
    },
    async handleEnroll(id) {
      if (!this.isCourseInAssignedLearningPath) {
        await Swal.fire({
          title: "Akses Dibatasi",
          text: "Anda hanya dapat mendaftar course yang terdaftar dalam Learning Path yang telah di-assign kepada Anda.",
          icon: "warning",
        });
        return;
      }

      const courseDetail = this.courseFetchStore.basicCourseDetail;
      // Cek prerequisite hanya untuk Intermediate & Advanced
      if (
        ["Intermediate", "Advanced"].includes(courseDetail.level) &&
        courseDetail.prerequisite_course
      ) {
        const prerequisiteEnrollment =
          this.enrollmentStore.enrollmentStudent.find(
            (enroll) =>
              enroll.courseID === courseDetail.prerequisite_course.course_id
          );

        // Kalau belum pernah enroll prerequisite
        if (!prerequisiteEnrollment) {
          await Swal.fire({
            title: "Prerequisite Required",
            text: `You must first enroll and complete "${this.courseFetchStore.getCourseTitleById(
              courseDetail.prerequisite_course
            )}" before enrolling in this course.`,
            icon: "warning",
          });
          return;
        }

        // Kalau sudah enroll tapi status belum completed
        if (prerequisiteEnrollment.status?.toLowerCase() !== "completed") {
          await Swal.fire({
            title: "Prerequisite Not Completed",
            text: `You must complete "${courseDetail.prerequisite_course.title}" before enrolling in this course.`,
            icon: "warning",
          });
          return;
        }
      }

      const result = await Swal.fire({
        title: "Are you sure?",
        text: "Do you want to enroll in this course?",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes, enroll me!",
        cancelButtonText: "Cancel",
      });

      if (!result.isConfirmed) {
        return; // User canceled
      }

      this.enrollmentStore.enrollmentForm.courseID_id = Number(id);
      this.enrollmentStore.enrollmentForm.studentID =
        this.authStore.currentUser.id;

      const duration = this.courseFetchStore.basicCourseDetail.duration;

      this.enrollmentStore.enrollmentForm.expiredAt =
        this.getExpiredDateFromDuration(duration);

      try {
        const response = await this.enrollmentStore.submitEnrollment();
        const enrollmetID = response.enrollmentID;
        console.log(response)

        const slug = btoa(`${id}-${enrollmetID}`);
        this.$router.push({
          name: "CourseWatch",
          params: {
            slug,
          },
        });
      } catch (error) {
        console.error("Gagal mendaftar:", error);
      }
    },
    getExpiredDateFromDuration(durationInDays) {
      const now = new Date();
      now.setDate(now.getDate() + durationInDays);
      return now.toISOString(); // Format ISO (untuk backend)
    },

    handleRequestAccessCourse(id) {
      if (this.hasPendingAccessRequest) {
        Swal.fire({
          title: "Permintaan Sedang Berjalan",
          text: "Kamu sudah memiliki permintaan akses yang sedang berjalan untuk course ini. Harap menunggu konfirmasi dari Principal.",
          icon: "info",
        });
        return;
      }

      Swal.fire({
        title: "Apakah kamu yakin?",
        text: "Kamu akan mengajukan akses ke course ini.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Ya, ajukan",
        cancelButtonText: "Batal",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            const courseIdToRequest =
              id || this.courseFetchStore.basicCourseDetail?.course_id;
            this.accessClassRequestStore.requestAccessForm.courseID =
              Number(courseIdToRequest);
            this.accessClassRequestStore.requestAccessForm.studentID =
              this.authStore.currentUser.id;

            await this.accessClassRequestStore.requestAccessCourse();

            await this.accessClassRequestStore.fetchAccessRequests({
              studentID: this.authStore.currentUser.id,
            });

            Swal.fire({
              title: "Berhasil!",
              text: "Permintaan akses berhasil dikirim.",
              icon: "success",
              timer: 2000,
              showConfirmButton: false,
            });
          } catch (error) {
            console.error("Gagal request access:", error);
            Swal.fire({
              title: "Gagal!",
              text:
                error?.response?.data?.detail ||
                "Gagal mengirim permintaan akses.",
              icon: "error",
            });
          }
        }
      });
    },
    isSubmissionExpired(submission) {
      if (!submission.endTime) return true; // kalau tidak ada field expiredAt, anggap expired
      const now = new Date();
      const expiredAt = new Date(submission.endTime);
      return now > expiredAt;
    },
    async openCertificateModal() {
      const modalEl = document.getElementById("view_certificate");
      const modal = Modal.getInstance(modalEl) || new Modal(modalEl);
      modal.show();
    },

    async handleDownloadCertificate() {
      try {
        this.isDownloadingCert = true;
        const studentId = this.authStore.currentUser?.id;
        const courseId = this.courseId;
        const courseTitle =
          this.courseFetchStore.basicCourseDetail?.title || "Course";
        const studentName =
          this.authStore.currentUser?.nickname ||
          this.authStore.currentUser?.username ||
          "Student";
        const filename = `Certificate_${courseTitle}_${studentName}.pdf`.replace(
          /\s+/g,
          "_"
        );

        // 1. Coba download file PDF langsung dari backend
        try {
          const blobData = await this.examDataStore.downloadCertificate(
            courseId,
            studentId
          );
          if (blobData && blobData.size > 0) {
            const blob = new Blob([blobData], { type: "application/pdf" });
            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(link.href);
            return;
          }
        } catch (backendErr) {
          console.warn(
            "Certificate belum ada di backend atau gagal download dari API, fallback ke canvas generator:",
            backendErr
          );
        }

        // 2. Fallback: generate dari canvas ref jika belum tersimpan di backend
        if (this.$refs.certComponent?.certRef) {
          const element = this.$refs.certComponent.certRef;
          const canvas = await html2canvas(element, {
            scale: 2,
            useCORS: true,
          });

          const imgData = canvas.toDataURL("image/png");
          const pdf = new jsPDF({
            orientation: canvas.width > canvas.height ? "landscape" : "portrait",
            unit: "px",
            format: [canvas.width, canvas.height],
          });

          pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);
          pdf.save(filename);

          // Simpan juga ke database di background
          const pdfBlob = pdf.output("blob");
          this.examDataStore.uploadCertificate(pdfBlob, courseId, studentId);
        } else {
          Swal.fire({
            title: "Gagal",
            text: "Komponen sertifikat tidak ditemukan untuk diunduh.",
            icon: "error",
          });
        }
      } catch (err) {
        console.error("Gagal mendownload sertifikat:", err);
        Swal.fire({
          title: "Error",
          text: "Terjadi kesalahan saat mengunduh sertifikat.",
          icon: "error",
        });
      } finally {
        this.isDownloadingCert = false;
      }
    },

    async goToExam() {
      if (this.isCompleted) {
        Swal.fire({
          title: "Course Selesai",
          text: "Kamu sudah menyelesaikan course ini dan telah lulus ujian.",
          icon: "info",
        });
        return;
      }

      const examID = this.courseFetchStore.basicCourseDetail?.exam?.examID;
      const studentID = this.authStore.currentUser.id;

      Swal.fire({
        title: "Mulai Ujian?",
        text: "Pastikan kamu sudah siap sebelum memulai. Waktu akan langsung berjalan setelah masuk ujian.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Ya, mulai sekarang",
        cancelButtonText: "Batal",
      }).then(async (result) => {
        if (result.isConfirmed) {
          await this.handleExamStart(examID, studentID, this.courseId);
        }
      });
    },

    async handleExamStart(examID, studentID, courseID) {
      try {
        const lastSubmission = await this.examDataStore.getLastSubmission(
          examID,
          studentID
        );
        // 🔎 Cek apakah sudah attempt 3 kali
        if (lastSubmission && lastSubmission.attempt >= 3) {
          await this.handleRemedialFlow(examID, courseID, studentID);
        } else {
          await this.handleNormalFlow(lastSubmission, examID, studentID);
        }
      } catch (error) {
        console.error("Gagal membuat submission:", error);
        Swal.fire("Error", "Gagal membuat submission. Coba lagi.", "error");
      }
    },

    async handleRemedialFlow(examID, CourseID, studentID) {
      const remedial = await this.accessClassRequestStore.getLatestRemedial(
        CourseID,
        studentID
      );

      if (remedial && remedial.status === "Approved" && !remedial.used) {
        // ✅ sudah dapat ijin remedial → buat submission baru
        const submission = await this.examDataStore.createSubmission(
          examID,
          studentID
        );
        this.startExam(submission, examID);
      } else {
        // ❌ belum ada remedial → minta request
        Swal.fire({
          title: "Batas Attempt Tercapai",
          text: "Kamu sudah 3x attempt. Ajukan request remedial ke Principal?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Ya, Ajukan",
          cancelButtonText: "Batal",
        }).then(async (res) => {
          if (res.isConfirmed) {
            await this.accessClassRequestStore.requestRemedial(
              CourseID,
              studentID
            );
          }
        });
      }
    },

    async handleNormalFlow(lastSubmission, examID, studentID) {
      let submission;
      if (lastSubmission && !this.isSubmissionExpired(lastSubmission)) {
        submission = lastSubmission;
      } else {
        submission = await this.examDataStore.createSubmission(
          examID,
          studentID
        );
      }

      this.startExam(submission, examID);
    },

    startExam(submission, examID) {
      const slug = btoa(`${this.courseId}-${examID}`);
      localStorage.setItem(
        "exam_session",
        JSON.stringify({
          examID: examID,
          submissionID: submission.submissionID,
          answers: [],
        })
      );

      this.$router.push({
        name: "CourseExam",
        params: { slug },
      });
    },
  },
};
</script>

<template>
  <layouts-header></layouts-header>
  <!-- Breadcrumb -->
  <div class="breadcrumb-bar text-center">
    <div class="container">
      <div class="row">
        <div class="col-md-12 col-12">
          <h2 class="breadcrumb-title mb-2">Course Detail</h2>
          <nav aria-label="breadcrumb">
            <ol class="breadcrumb justify-content-center mb-0">
              <li class="breadcrumb-item">
                <router-link to="/home">Home</router-link>
              </li>
              <li class="breadcrumb-item active" aria-current="page">
                Course Detail
              </li>
            </ol>
          </nav>
        </div>
      </div>
    </div>
  </div>
  <!-- /Breadcrumb -->

  <!-- Course detail -->
  <section class="course-details-two">
    <div class="container">
      <div class="row">
        <div class="col-12">
          <div class="card bg-light">
            <div class="card-body d-lg-flex align-items-center">
              <div class="w-100 ps-lg-4">
                <h3 class="mb-2">
                  [{{ courseFetchStore.basicCourseDetail.course_group?.code }}]
                  {{ courseFetchStore.basicCourseDetail.title }}
                </h3>
                <p class="fs-14 mb-3">
                  {{ courseFetchStore.basicCourseDetail.short_description }}
                </p>
                <div
                  class="d-flex align-items-center gap-2 gap-sm-3 gap-xl-4 flex-wrap my-3 my-sm-0"
                >
                  <p class="fw-medium d-flex align-items-center mb-0">
                    <img
                      class="me-2"
                      src="@/assets/img/icons/book.svg"
                      alt="img"
                    />{{ courseFetchStore.basicCourseDetail.content_count }}
                    Lesson
                  </p>
                  <p class="fw-medium d-flex align-items-center mb-0">
                    <img
                      class="me-2"
                      src="@/assets/img/icons/people.svg"
                      alt="img"
                    />{{
                      courseFetchStore.basicCourseDetail.student_enrolled_count
                    }}
                    students enrolled
                  </p>
                  <p
                    class="fw-medium d-flex align-items-center mb-0"
                    v-if="
                      authStore.role?.toLowerCase() === 'student' &&
                      alreadyEnrolled &&
                      expiredDate
                    "
                  >
                    <img
                      class="me-2"
                      src="@/assets/img/icons/timer-start3.svg"
                      alt="img"
                    />{{ sharedStore.formatTanggal(expiredDate) }}
                  </p>
                  <span
                    class="badge badge-sm rounded-pill bg-warning fs-12"
                    v-if="courseFetchStore.basicCourseDetail.level"
                    >{{ courseFetchStore.basicCourseDetail.level }}</span
                  >
                </div>
                <div
                  class="d-sm-flex align-items-center justify-content-sm-between mt-3"
                >
                  <div class="d-flex align-items-center">
                    <div class="avatar avatar-lg">
                      <img
                        class="rounded-circle"
                        :src="`${
                          authStore.authUrl
                        }/storage/photo/${authStore.userPhoto(
                          courseFetchStore.basicCourseDetail.instructor
                        )}`"
                        alt="img"
                      />
                    </div>
                    <div class="ms-2">
                      <h5 class="fs-18 fw-semibold">
                        {{
                          authStore.userName(
                            courseFetchStore.basicCourseDetail.instructor
                          )
                        }}
                      </h5>
                      <p class="fs-14">Instructor</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="row mt-4">
        <div class="col-lg-8">
          <div class="text-center">
            <img
              :src="`${authStore.Url}/media/${courseFetchStore.basicCourseDetail.thumbnail}`"
              alt="img"
              class="img-fluid mb-4"
            />
          </div>
          <div class="course-page-content pt-0">
            <div class="card mb-4">
              <div class="card-body">
                <h5 class="mb-3">Overview</h5>
                <h6 class="mb-2">Course Description</h6>
                <div
                  v-html="courseFetchStore.basicCourseDetail.description"
                ></div>
              </div>
            </div>
            <div class="card mb-4">
              <div class="card-body">
                <div class="d-flex justify-content-between flex-wrap">
                  <h5 class="subs-title mb-2 mb-sm-3">Course Content</h5>
                  <h6 class="fs-16 fw-medium text-gray-7 mb-3">
                    {{ courseFetchStore.basicCourseDetail.content_count }}
                    Lectures
                  </h6>
                </div>
                <div
                  class="accordion accordion-customicon1 accordions-items-seperate p-0"
                  id="accordioncustomicon1Example"
                >
                  <div class="accordion-body p-0">
                    <ul>
                      <li
                        class="p-4 px-3 d-flex justify-content-between"
                        v-for="(item, index) in courseFetchStore
                          .basicCourseDetail.content"
                        :key="index"
                      >
                        <p class="mb-0">
                          <img
                            class="me-2"
                            src="@/assets/img/icons/play.svg"
                            alt="img"
                          />Lecture {{ index + 1 }}: {{ item.title }}
                        </p>
                        <!-- <div class="d-flex gap-xl-5 gap-3">
                          <a href="#" class="preview-link">Preview</a>
                          <p class="mb-0">02:53</p>
                        </div> -->
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-lg-4">
          <div class="">
            <div class="course-sidebar-sec mt-0">
              <div
                class="course-actions"
                v-if="
                  primaryAction ||
                  secondaryActions.length ||
                  (canRequestAccess && hasPendingAccessRequest) ||
                  (isStudent &&
                    !alreadyEnrolled &&
                    !isCourseInAssignedLearningPath &&
                    courseFetchStore.basicCourseDetail?.status === 'Active')
                "
              >
                <!-- Alert jika course tidak terdaftar di learning path student -->
                <div
                  v-if="
                    isStudent &&
                    !alreadyEnrolled &&
                    !isCourseInAssignedLearningPath &&
                    courseFetchStore.basicCourseDetail?.status === 'Active'
                  "
                  class="alert alert-warning border d-flex align-items-start py-2 px-3 mb-2"
                  role="alert"
                >
                  <i class="isax isax-info-circle me-2 mt-1 text-warning fs-18"></i>
                  <div class="small lh-sm">
                    <strong class="d-block text-dark">Course Terbatas</strong>
                    <span class="text-muted">
                      Course ini hanya dapat diikuti jika telah di-assign ke dalam Learning Path Anda.
                    </span>
                  </div>
                </div>
                <div
                  v-if="canRequestAccess && hasPendingAccessRequest"
                  class="course-actions__status alert alert-light border d-flex align-items-start py-2 px-3 mb-0"
                  role="status"
                >
                  <i class="isax isax-clock me-2 mt-1 text-secondary"></i>
                  <div class="small lh-sm">
                    <strong class="d-block text-dark">Access request pending</strong>
                    <span class="text-muted"
                      >Waiting for approval before you can continue.</span
                    >
                  </div>
                </div>

                <!-- Primary CTA: only one solid full-width action -->
                <button
                  v-if="primaryAction?.key === 'approve'"
                  type="button"
                  class="btn btn-success w-100 btn-enroll d-flex align-items-center justify-content-center"
                  data-bs-toggle="modal"
                  data-bs-target="#approveCourseModalDetail"
                >
                  <i class="isax isax-tick-circle me-2"></i>
                  {{ primaryAction.label }}
                </button>
                <button
                  v-else-if="primaryAction"
                  type="button"
                  class="btn w-100 btn-enroll d-flex align-items-center justify-content-center"
                  :class="primaryAction.btnClass"
                  @click="handleCourseAction(primaryAction.key)"
                >
                  <i
                    v-if="primaryAction.icon"
                    :class="[primaryAction.icon, 'me-2']"
                  ></i>
                  {{ primaryAction.label }}
                </button>

                <!-- Secondary / supporting actions -->
                <div
                  v-if="secondaryActions.length"
                  class="course-actions__secondary"
                >
                  <button
                    v-for="action in secondaryActions"
                    :key="action.key"
                    type="button"
                    class="btn w-100 d-flex align-items-center justify-content-center"
                    :class="action.btnClass"
                    @click="handleCourseAction(action.key)"
                  >
                    {{ action.label }}
                  </button>
                </div>
              </div>
              <div class="card">
                <div class="card-body cou-features">
                  <h5 class="subs-title">Course Features</h5>
                  <ul>
                    <li>
                      <p class="mb-0">
                        <img
                          class="me-2"
                          src="@/assets/img/icons/people2.svg"
                          alt="img"
                        />Enrolled:
                        {{
                          courseFetchStore.basicCourseDetail
                            .student_enrolled_count
                        }}
                        students
                      </p>
                    </li>
                    <li>
                      <p class="mb-0">
                        <img
                          class="me-2"
                          src="@/assets/img/icons/note.svg"
                          alt="img"
                        />Lesson:
                        {{ courseFetchStore.basicCourseDetail.content_count }}
                      </p>
                    </li>
                    <li>
                      <p
                        class="mb-0"
                        v-if="courseFetchStore.basicCourseDetail.level"
                      >
                        <img
                          class="me-2"
                          src="@/assets/img/icons/chart.svg"
                          alt="img"
                        />Level: {{ courseFetchStore.basicCourseDetail.level }}
                      </p>
                    </li>
                    <li>
                      <p
                        class="mb-0"
                        v-if="
                          courseFetchStore.basicCourseDetail.prerequisite_course
                        "
                      >
                        <img
                          class="me-2"
                          src="@/assets/img/icons/play3.svg"
                          alt="img"
                        />Prerequisite:
                        {{
                          courseFetchStore.getCourseTitleById(
                            courseFetchStore.basicCourseDetail
                              .prerequisite_course
                          )
                        }}
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
  <!-- /Course detail -->\

  <footer-two></footer-two>
  <!-- View Certificate -->
  <div class="modal fade" id="view_certificate">
    <div class="modal-dialog modal-dialog-centered modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h5>View Certificate</h5>
          <button
            type="button"
            class="btn-close custom-btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="isax isax-close-circle5"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="d-flex justify-content-center align-items-center">
            <certificates
              ref="certComponent"
              :name="authStore.currentUser?.nickname"
              :company="
                authStore.currentUser?.company ||
                authStore.currentUser?.cmp_code
              "
              :course="courseFetchStore.basicCourseDetail.title"
              :date="sharedStore.formatTanggal(new Date())"
              :instructor="
                authStore.userName(
                  courseFetchStore.basicCourseDetail?.instructor
                )
              "
            />
          </div>
          <div class="d-flex justify-content-end gap-2 mt-4">
            <button
              type="button"
              class="btn btn-secondary rounded-pill px-4"
              data-bs-dismiss="modal"
            >
              Tutup
            </button>
            <button
              type="button"
              class="btn btn-warning rounded-pill px-4 d-inline-flex align-items-center"
              @click="handleDownloadCertificate"
              :disabled="isDownloadingCert"
            >
              <i
                class="isax isax-document-download me-2"
                v-if="!isDownloadingCert"
              ></i>
              <span
                class="spinner-border spinner-border-sm me-2"
                v-if="isDownloadingCert"
                role="status"
              ></span>
              {{ isDownloadingCert ? "Mengunduh..." : "Download Certificate" }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
  <BaseLoading v-show="sharedStore.loading"></BaseLoading>

  <!-- Modal Approve Course Basic Information di Course Detail -->
  <div
    class="modal fade"
    id="approveCourseModalDetail"
    tabindex="-1"
    aria-labelledby="approveCourseModalDetailLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content border-0 shadow">
        <div class="modal-header bg-light">
          <div>
            <h5 class="modal-title fw-bold" id="approveCourseModalDetailLabel">
              <i class="isax isax-information text-primary me-2"></i>Review Basic Information Course
            </h5>
            <p class="text-muted fs-13 mb-0">
              Pastikan informasi detail kursus sudah benar sebelum melakukan persetujuan (approve).
            </p>
          </div>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
            id="closeApproveDetailModalBtn"
          ></button>
        </div>
        <div class="modal-body p-4" v-if="courseFetchStore.basicCourseDetail">
          <!-- Course Header & Thumbnail -->
          <div class="card border mb-3">
            <div class="card-body">
              <div class="d-flex flex-column flex-md-row gap-3 align-items-start">
                <div
                  class="rounded overflow-hidden flex-shrink-0 border bg-light d-flex align-items-center justify-content-center"
                  style="width: 140px; height: 95px;"
                >
                  <img
                    v-if="courseFetchStore.basicCourseDetail.thumbnail"
                    class="img-fluid object-fit-cover w-100 h-100"
                    :src="`${authStore.Url}/media/${courseFetchStore.basicCourseDetail.thumbnail}`"
                    alt="Course Thumbnail"
                  />
                  <div v-else class="text-muted text-center p-2">
                    <i class="isax isax-image fs-24 d-block mb-1"></i>
                    <span class="fs-11">No Image</span>
                  </div>
                </div>
                <div class="flex-grow-1">
                  <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
                    <span class="badge bg-warning text-dark">
                      Status: {{ courseFetchStore.basicCourseDetail.status }}
                    </span>
                    <span
                      v-if="courseFetchStore.basicCourseDetail.course_group"
                      class="badge bg-info-subtle text-info border border-info"
                    >
                      Group: {{ courseFetchStore.basicCourseDetail.course_group.name }} ({{ courseFetchStore.basicCourseDetail.course_group.code }})
                    </span>
                  </div>
                  <h5 class="fw-bold mb-1 text-dark">{{ courseFetchStore.basicCourseDetail.title }}</h5>
                  <p class="text-muted fs-13 mb-2" v-if="courseFetchStore.basicCourseDetail.short_description">
                    {{ courseFetchStore.basicCourseDetail.short_description }}
                  </p>
                  <div class="d-flex flex-wrap gap-3 fs-13 text-secondary">
                    <div>
                      <i class="isax isax-user me-1 text-primary"></i>
                      <strong>Instructor:</strong> {{ authStore.userName(courseFetchStore.basicCourseDetail.instructor) }}
                    </div>
                    <div>
                      <i class="isax isax-calendar me-1 text-primary"></i>
                      <strong>Dibuat:</strong> {{ formatDate(courseFetchStore.basicCourseDetail.created_at) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Basic Details Grid -->
          <div class="row g-3 mb-3">
            <div class="col-sm-6 col-md-3">
              <div class="p-3 border rounded bg-light text-center h-100">
                <div class="text-muted fs-12 mb-1">Level</div>
                <div class="fw-bold text-dark">{{ courseFetchStore.basicCourseDetail.level || '-' }}</div>
              </div>
            </div>
            <div class="col-sm-6 col-md-3">
              <div class="p-3 border rounded bg-light text-center h-100">
                <div class="text-muted fs-12 mb-1">Durasi</div>
                <div class="fw-bold text-dark">{{ courseFetchStore.basicCourseDetail.duration ? `${courseFetchStore.basicCourseDetail.duration} Hari` : '-' }}</div>
              </div>
            </div>
            <div class="col-sm-6 col-md-3">
              <div class="p-3 border rounded bg-light text-center h-100">
                <div class="text-muted fs-12 mb-1">Prerequisite Course</div>
                <div class="fw-bold text-dark">{{ courseFetchStore.basicCourseDetail.prerequisite_course?.title || courseFetchStore.basicCourseDetail.prerequisite_course || '-' }}</div>
              </div>
            </div>
            <div class="col-sm-6 col-md-3">
              <div class="p-3 border rounded bg-light text-center h-100">
                <div class="text-muted fs-12 mb-1">Expired Date</div>
                <div class="fw-bold text-dark">{{ formatDate(courseFetchStore.basicCourseDetail.expired_date) }}</div>
              </div>
            </div>
          </div>

          <!-- Description Section -->
          <div class="card border mb-3" v-if="courseFetchStore.basicCourseDetail.description">
            <div class="card-header bg-white py-2">
              <h6 class="fw-semibold mb-0 fs-14">
                <i class="isax isax-document-text me-1 text-primary"></i>Deskripsi Course
              </h6>
            </div>
            <div class="card-body fs-14 text-secondary" v-html="courseFetchStore.basicCourseDetail.description"></div>
          </div>

          <!-- Course Content Section -->
          <div class="card border mb-3">
            <div class="card-header bg-white py-2 d-flex justify-content-between align-items-center">
              <h6 class="fw-semibold mb-0 fs-14">
                <i class="isax isax-book me-1 text-primary"></i>Konten & Modul Pembelajaran
              </h6>
              <span class="badge bg-secondary">
                {{ courseFetchStore.basicCourseDetail.content?.length || courseFetchStore.basicCourseDetail.content_count || 0 }} Materi
              </span>
            </div>
            <div class="card-body p-0">
              <div
                v-if="!courseFetchStore.basicCourseDetail.content || courseFetchStore.basicCourseDetail.content.length === 0"
                class="p-3 text-center text-muted fs-13"
              >
                Belum ada konten materi yang diunggah.
              </div>
              <ul v-else class="list-group list-group-flush">
                <li
                  v-for="(content, cIdx) in courseFetchStore.basicCourseDetail.content"
                  :key="cIdx"
                  class="list-group-item d-flex justify-content-between align-items-center py-2"
                >
                  <div class="d-flex align-items-center">
                    <span class="badge bg-light text-dark border me-2">#{{ content.sequence || (cIdx + 1) }}</span>
                    <div>
                      <div class="fw-medium fs-14">{{ content.title }}</div>
                      <div class="text-muted fs-12">
                        <span class="badge bg-light text-muted border me-1">{{ content.type }}</span>
                        <span v-if="content.isSkippable" class="badge bg-success-subtle text-success">Skippable</span>
                        <span v-else class="badge bg-warning-subtle text-warning">Non-skippable</span>
                      </div>
                    </div>
                  </div>
                  <div class="text-end">
                    <span
                      class="fs-12 text-muted text-truncate d-inline-block"
                      style="max-width: 220px;"
                      :title="content.filePath"
                    >
                      {{ content.filePath ? content.filePath.split('/').pop() : '-' }}
                    </span>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          <!-- Exam Section -->
          <div class="card border mb-0">
            <div class="card-header bg-white py-2">
              <h6 class="fw-semibold mb-0 fs-14">
                <i class="isax isax-award me-1 text-primary"></i>Informasi Ujian Akhir (Exam)
              </h6>
            </div>
            <div class="card-body">
              <div v-if="courseFetchStore.basicCourseDetail.exam" class="p-3 border rounded bg-light mb-2">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <span class="fw-bold text-dark fs-14">Ujian: {{ courseFetchStore.basicCourseDetail.exam.course_name || courseFetchStore.basicCourseDetail.title }}</span>
                  <span class="badge bg-primary">{{ courseFetchStore.basicCourseDetail.exam.examType }}</span>
                </div>
                <div class="row g-2 fs-13 text-secondary">
                  <div class="col-md-4">
                    <strong>Batas Waktu:</strong> {{ courseFetchStore.basicCourseDetail.exam.time_limit_minutes }} Menit
                  </div>
                  <div class="col-md-4">
                    <strong>Bobot Pilihan Ganda:</strong> {{ courseFetchStore.basicCourseDetail.exam.multiple_choice_weight }}%
                  </div>
                  <div class="col-md-4">
                    <strong>Bobot Essay:</strong> {{ courseFetchStore.basicCourseDetail.exam.essay_weight }}%
                  </div>
                </div>
              </div>
              <div v-else class="text-muted fs-13 mb-2">
                Tidak ada ujian akhir (exam) yang dikonfigurasi.
              </div>

              <div class="fs-13 text-secondary" v-if="courseFetchStore.basicCourseDetail.exam">
                <i class="isax isax-document-text me-1 text-primary"></i>
                <strong>Jumlah Soal Exam:</strong> {{ courseFetchStore.basicCourseDetail.exam.question_count ?? courseFetchStore.basicCourseDetail.exam_question_count ?? 0 }} Soal
                <span v-if="courseFetchStore.basicCourseDetail.exam.examType !== 'Practical' && (courseFetchStore.basicCourseDetail.exam.question_count || courseFetchStore.basicCourseDetail.exam_question_count)" class="text-muted ms-1">
                  ({{ courseFetchStore.basicCourseDetail.exam.mc_question_count || 0 }} Pilihan Ganda, {{ courseFetchStore.basicCourseDetail.exam.essay_question_count || 0 }} Essay)
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer bg-light">
          <button
            type="button"
            class="btn btn-outline-danger me-2"
            data-bs-dismiss="modal"
          >
            Batal
          </button>
          <button
            type="button"
            class="btn btn-success d-inline-flex align-items-center"
            :disabled="isApproving"
            @click="confirmApproveInDetail"
          >
            <i class="isax isax-tick-circle me-1" v-if="!isApproving"></i>
            <span class="spinner-border spinner-border-sm me-1" v-if="isApproving" role="status"></span>
            {{ isApproving ? 'Memproses...' : 'Lanjut Approve' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.course-actions {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.course-actions__secondary {
  display: grid;
  gap: 0.5rem;
}

.course-actions__secondary .btn {
  min-height: 42px;
  font-weight: 500;
}

.course-actions__status {
  border-radius: 0.5rem;
  background-color: #f8f9fa;
}
</style>
